import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inbox, identity } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Group, Button, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Select, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { openModal, closeModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { shortId } from '../../../lib/format';

export interface AgentOption {
  user_id: string;
  label: string;
  status_code: string;
}

/**
 * Everyone on the team who can sign in, for every agent picker (8 October 2026). Names and emails live in identity;
 * the inbox service adds whether someone takes new conversations. The list used to be the inbox's agent profiles
 * only, which exist once a person sets their availability, so a new business had nobody to assign or transfer to.
 */
export function useAgentOptions(): { options: AgentOption[]; isLoading: boolean } {
  const agents = useQuery({ queryKey: ['agents'], queryFn: () => inbox.listAgents() });
  const users = useQuery({ queryKey: ['team'], queryFn: () => identity.listUsers({ limit: 200 }) });

  const options = useMemo(() => {
    const status = new Map((agents.data?.data ?? []).map((a) => [a.user_id, a.status_code] as const));
    return (users.data?.data ?? [])
      .filter((u) => !u.is_locked)
      .map<AgentOption>((u) => ({ user_id: u.id, status_code: status.get(u.id) ?? '', label: u.display_name || u.email || shortId(u.id) }));
  }, [agents.data, users.data]);

  return { options, isLoading: agents.isLoading || users.isLoading };
}

interface TransferFormProps {
  conversationId: string;
  currentAssignee: string | null;
  onClose: () => void;
}

function TransferForm({ conversationId, currentAssignee, onClose }: TransferFormProps) {
  const t = useT();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { options, isLoading } = useAgentOptions();
  const candidates = options.filter((o) => o.user_id !== currentAssignee);
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');

  const transfer = useMutation({
    mutationFn: async () => {
      const target = candidates.find((c) => c.user_id === to);
      const reason = note.trim() || undefined;
      await inbox.transferConversation(conversationId, to, reason);
      // The audit row keeps the reason; a note makes it visible to the receiving agent.
      if (reason) {
        await inbox.addNote(
          conversationId,
          `${t('inbox.transfer.noteprefix')} ${target?.label ?? shortId(to)}: ${reason}`,
          user?.display_name ?? user?.email ?? undefined
        );
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      notifySuccess(t('inbox.transfer.done'), t('inbox.title'));
      onClose();
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('inbox.transfer.transferFailed'), t('inbox.title'))
  });

  return (
    <Stack gap="md">
      {!isLoading && candidates.length === 0 && (
        <Alert color="info">{t('inbox.transfer.noAgents')}</Alert>
      )}
      <Select
        label={t('inbox.transfer.agent')}
        value={to}
        onChange={(e) => setTo(e.currentTarget.value)}
        disabled={isLoading || candidates.length === 0}
        required
        data={[
          { value: '', label: '–' },
          ...candidates.map((c) => ({ value: c.user_id, label: c.status_code === 'available' ? `${c.label} · ${t('inbox.available.short')}` : c.label }))
        ]}
      />
      <Textarea
        label={t('inbox.transfer.note')}
        value={note}
        onChange={(e) => setNote(e.currentTarget.value)}
        minRows={2}
        maxLength={2000}
      />
      <Text role="caption" c="var(--rr-color-text-muted)">{t('inbox.transfer.keeps')}</Text>
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
        <Button onClick={() => transfer.mutate()} loading={transfer.isPending} disabled={!to}>
          {t('inbox.transfer')}
        </Button>
      </Group>
    </Stack>
  );
}

/** Imperative entry point: `<ModalsProvider>` is mounted at the app root. */
export function openTransferModal(args: { conversationId: string; currentAssignee: string | null; title: string }): void {
  const id = openModal({
    title: args.title,
    size: 'sm',
    children: (
      <TransferForm
        conversationId={args.conversationId}
        currentAssignee={args.currentAssignee}
        onClose={() => closeModal(id)}
      />
    )
  });
}
